/**
 * @file  surcharges/index.ts
 * @description  the surcharge service to manage options, inUsed and preselected options
 *
 * @author ogarcia (ozkary)
 * @date 20220707 initial implementation
 */

/**
 * Imports.
 */
import { Surcharge } from '../../functions/quotes/quotes.model';
import * as ProductModels from '../../functions/product/product.model';
import { selectedOptions, validOptions } from './options';
import * as surchargesJson from './surcharges.json';
import { getProduct } from '../product';
import { getSingleQuote } from '../quotes';

/**
 * List of valid pre-rating option
 */
export const validPreRating = surchargesJson?.preRating ?? [];

/**
 * get the cached quote and retrive the surcharges
 * @returns the list of options
 */

/**
 * Returns surcharges associated to a coverage or outside of it
 * Merge all the surcharges to make it easy consume by the UI
 * @param coverage optional coverage code
 * @param terms optional term to get momthly value
 * @returns surcharges in the coverage or outside
 */
const getDataFromCache = async (
  coverage = '',
  terms = 1,
  includeMandatory = false,
  downRate = 0
): Promise<Surcharge[]> => {
  const product = getProduct();
  const programs = await getSingleQuote(false, product?.contractCode);
  let surcharges: Surcharge[] = [];

  if (programs) {
    // get all the surcharges outside coverages
    if (includeMandatory && programs.Surcharges) {
      surcharges = programs.Surcharges;
    }

    // find the coverage surcharges and merge the results
    if (programs && programs.Coverages) {
      const covSurcharges = programs.Coverages.find(
        (item) => item['@Code'] === coverage
      )?.Surcharges;

      if (covSurcharges && covSurcharges.length > 0) {
        surcharges = [...surcharges, ...covSurcharges];
      }
    }
    // update the monthly value
    surcharges = surcharges.map((item) => {
      const value = Number(item.Value ?? 0);
      const valueAfterDown = value - (value * downRate);
      item.MonthlyValue = valueAfterDown / terms;
      return item;
    });
  }
  return surcharges;
};

/**
 * surcharge operations
 */

/**
 * Returns the surcharges that are included in the price. These items
 * should be sent to the contract and displayed as part of the selected plan
 * @param includeMandatory true to returns items that are not set to isUsed=1
 * @returns surcharges that have the property InUsed=1
 */
export const getIncludedItems = async (includeMandatory = false): Promise<Surcharge[]> => {
  let items: Surcharge[] = await getDataFromCache('', 1, true);
  items = includeMandatory ? items : items?.filter((s) => s.IsUsed !== undefined && s.IsUsed === '1');
  return items;
};

/**
 * Returns the surcharges associated to a coverage.
 * This also includes the optional surcharges outside the coverage (Excludes InUsed=1)
 * @param coverage coverage code
 * @param terms to calculate monthly value
 * @returns the surcharges associated with a coverage
 */
export const getCoverageItems = async (
  coverage: string,
  terms = 1,
  downRate = 0
): Promise<Surcharge[]> => {
  const items: Surcharge[] = await getDataFromCache(coverage, terms, false, downRate);
  return items.filter((i) => i.IsUsed === undefined || i.IsUsed !== '1');
};

/**
 * Call the function to get all the contracts that should be included in the contract.
 * Included items + selected options
 * @returns all the surcharges that should be sent to the contract
 */
export const getContractSurcharges = async (): Promise<Surcharge[]> => {
  let items: Surcharge[] = await getIncludedItems(false);
  const options = await selectedOptions();
  items = [...items, ...options];
  return items;
};

/**
 * Gerts current contract surcharges.
 * @returns Preselected surcharges.
 */
export const getPreselectedSurcharges = async (surcharges?: ProductModels.Surcharge[]): Promise<Surcharge[]> => {
  return (
    surcharges?.map((sur) => {
      return {
        Code: sur.code,
        Description: sur.description,
        Value: sur.amount || '',
        Preselected: validOptions.indexOf(sur.code || '') > -1 // true
      } as Surcharge;
    }) ?? []
  );
};

/**
 * Validate the pre-rating options. Configure additional options on the surcharges.json file
 * @returns Pre-rating surcharges.
 */
export const getPreRatingOptions = async (surcharges?: ProductModels.Surcharge[]): Promise<Surcharge[]> => {
  const validSurcharges = surcharges?.filter((sur) => validPreRating.indexOf(sur.code || '') > -1);
  const preRateOptions: Surcharge[] = validSurcharges?.map((s) => {
    return {
      Code: s.code,
      Value: s.amount || '',
      Description: s.description
    } as Surcharge;
  }) ?? [];

  return preRateOptions;
};
